
import { useState, useEffect, useCallback } from 'react';
import { GamePlan } from '@/types/gamePlanner';
import { useToast } from '@/hooks/use-toast';
import { useOfflineGamePlans, useOfflineGamePlanTemplates } from '@/contexts/offline';
import { usePermissions } from '@/contexts/unified-permissions';
import { transformFromOfflineGamePlan } from '@/services/gamePlanner/offlineGamePlanService';

export function usePlansLoader() {
  const [gamePlans, setGamePlans] = useState<GamePlan[]>([]);
  const [templates, setTemplates] = useState<GamePlan[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { user } = usePermissions();

  // Use offline hooks for data fetching
  const {
    data: offlineGamePlans,
    loading: gamePlansLoading,
    error: gamePlansError,
    refresh: refreshGamePlans
  } = useOfflineGamePlans(user?.id);

  const {
    data: offlineTemplates,
    loading: templatesLoading,
    error: templatesError,
    refresh: refreshTemplates
  } = useOfflineGamePlanTemplates(user?.id);

  // Transform offline data to GamePlan format
  useEffect(() => {
    if (offlineGamePlans) {
      const transformedPlans = offlineGamePlans.map(transformFromOfflineGamePlan);
      setGamePlans(transformedPlans);
    }
  }, [offlineGamePlans]);

  useEffect(() => {
    if (offlineTemplates) {
      const transformedTemplates = offlineTemplates.map(transformFromOfflineGamePlan);
      setTemplates(transformedTemplates);
    }
  }, [offlineTemplates]);

  // Handle errors
  useEffect(() => {
    if (gamePlansError) {
      console.error('Error loading game plans:', gamePlansError);
      toast({
        title: 'Error',
        description: 'Failed to load your game plans.',
        variant: 'destructive',
      });
    }
  }, [gamePlansError, toast]);

  useEffect(() => {
    if (templatesError) {
      console.error('Error loading templates:', templatesError);
      toast({
        title: 'Error',
        description: 'Failed to load templates.',
        variant: 'destructive',
      });
    }
  }, [templatesError, toast]);

  // Update loading state
  useEffect(() => {
    setLoading(gamePlansLoading || templatesLoading);
  }, [gamePlansLoading, templatesLoading]);

  const loadGamePlans = useCallback(async () => {
    await refreshGamePlans();
  }, [refreshGamePlans]);

  const loadTemplates = useCallback(async () => {
    await refreshTemplates();
  }, [refreshTemplates]);

  return {
    gamePlans,
    setGamePlans,
    templates,
    setTemplates,
    loading,
    loadGamePlans,
    loadTemplates
  };
}
